import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc"; // Import the UTC plugin
import timezone from "dayjs/plugin/timezone"; // Import the timezone plugin
import customParseFormat from "dayjs/plugin/customParseFormat"; // For parsing custom formats

import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

// Extend Day.js with the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

dayjs.extend(timezone); // Extend dayjs with the timezone plugin

dayjs.extend(utc); // Extend dayjs with the UTC plugin
dayjs.extend(customParseFormat); // Extend dayjs with the custom parse format plugin

export function formatDateFromString(dateString: string): string {
  if (dateString === undefined || dateString === "") {
    return "-";
  }
  const date = dayjs.utc(dateString).local(); // Parse the date as UTC and convert to local time

  // Get the day of the month
  const day = date.date();
  // Format the day with the appropriate suffix
  const dayWithSuffix = day + getDaySuffix(day);

  return `${dayWithSuffix} ${date.format("MMMM YYYY")}`;
}

// Helper function to get the day suffix (st, nd, rd, th)
function getDaySuffix(day: number): string {
  if (day > 3 && day < 21) return "th"; // 11th, 12th, 13th etc.
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export function getUrlPathId(url: string) {
  const parts = url.split("/");
  return parts[parts.length - 1];
}

export const formatDate = (date: string) => {
  const value = dayjs(date).format("Do MMMM YYYY");
  if (value === "Invalid Date") {
    return "-";
  } else {
    return value;
  }
};

export function formatParsedDateTime(dateTimeString: string): string {
  const dateTime = dayjs(dateTimeString);
  return dateTime.format("ddd DD MMMM YYYY, hh:mm");
}

export const formatTime = (dateTimeString: string) => {
  const utcDateTime = dayjs.utc(dateTimeString);
  return utcDateTime.format("hh:mm A");
};

export const formatActualTime = (dateTimeString: string) => {
  if (dateTimeString === undefined || null || "") return "-";
  const localDateTime = dayjs.utc(dateTimeString).local();
  return localDateTime.format("hh:mm A");
};

export const formatDateWithTime = (dateTimeString: string) => {
  if (!dateTimeString) return "-";

  const local = dayjs.utc(dateTimeString).local();
  if (!local.isValid()) return "-";

  return local.format("MMM DD, YYYY · hh:mm A");
};

export function formatDateTime(dateTimeString: string): string {
  try {
    // Parse the date string as UTC
    const utcDateTime = dayjs.utc(dateTimeString);
    return utcDateTime.format("Do MMMM YYYY, h:mm A");
  } catch (error) {
    console.error(error);
    return "-";
  }
}

export function formatTextType(input: string): string {
  if (typeof input != "string") {
    return "-";
  }
  if (input === "") {
    return "-";
  }
  if (input?.includes("_")) {
    // If the string contains underscores
    return input
      .split("_") // Split by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(" "); // Join with spaces
  } else {
    // If the string does not contain underscores
    return input?.charAt(0).toUpperCase() + input.slice(1).toLowerCase(); // Capitalize first letter only
  }
}

export function limitToSeven(str: string): string {
  if (typeof str != "string") return "";
  if (str.length < 7) return "";
  return str.substring(0, 7);
}

export function firstandLastName(name?: string): string {
  if (!name || name.trim() === "") {
    return "MM";
  }

  const parts = name.trim().split(/\s+/);

  const firstInitial = parts[0]?.[0]?.toUpperCase() ?? "";
  const secondInitial = parts[1]?.[0]?.toUpperCase() ?? "";

  return firstInitial + secondInitial || firstInitial;
}

export function formatFullName(firstName: string, lastName: string): string {
  if (!firstName || !lastName) {
    return "";
  }

  const firstInitial = firstName[0]?.toUpperCase() ?? "";
  const restOfFirstName = firstName.slice(1).toLowerCase();
  const lastInitial = lastName[0]?.toUpperCase() ?? "";
  const restOfLastName = lastName.slice(1).toLowerCase();

  return `${firstInitial}${restOfFirstName} ${lastInitial}${restOfLastName}`;
}

export function separateDocsByComma(input: string): string[] {
  if (!input) {
    return [];
  }

  const items = input.split(",");
  return items.map((item) => item.trim().replace(/_/g, " ")).filter(Boolean);
}

export function extractContractDates(input: string): string | null {
  const regex =
    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z) - (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z)/;
  const match = input.match(regex);

  if (!match) {
    return null;
  }

  const [startDate, endDate] = match.slice(1);

  const formattedStartDate = dayjs(startDate).format("Do MMMM YYYY");
  const formattedEndDate = dayjs(endDate).format("Do MMMM YYYY");

  return `${formattedStartDate} - ${formattedEndDate}`;
}

interface DataItem {
  _id: string;
  count: number;
}

// Function to extract _id values
export const getIds = (data: DataItem[]): string[] => {
  return data.map((item) => item._id);
};

// Function to extract count values
export const getCounts = (data: DataItem[]): number[] => {
  return data.map((item) => item.count);
};

export const formatDecimal = (value: string) => {
  if (!value) {
    return "-";
  }
  return parseFloat(value).toFixed(1);
};

export const formatDecimalNumber = (value: number) => {
  if (value === 0) {
    return 0;
  }
  if (isNaN(value) || !isFinite(value)) {
    return "-";
  }
  return parseFloat(`${value}`).toFixed(1);
};

export const formatDecimalNum = (value: number) => {
  if (value === 0) {
    return 0;
  }

  const num = value.toFixed(1);

  return Math.round(Number(num)); // or use Math.floor or Math.ceil as preferred
};

export function isDateWithinRange(
  date: dayjs.Dayjs,
  startDate: string,
  endDate: string,
): boolean {
  // Convert start and end dates to Day.js objects for accurate comparison
  const dateToCheck = date.startOf("day"); // Ensure date has no time part
  const rangeStart = dayjs(startDate).startOf("day");
  const rangeEnd = dayjs(endDate).startOf("day");

  // Check if the date falls within or matches the range
  return (
    dateToCheck.isSameOrAfter(rangeStart) &&
    dateToCheck.isSameOrBefore(rangeEnd)
  );
}

// export function updateDatePreservingTime(
//   selectedDate: Dayjs,
//   time: Dayjs | null
// ): Dayjs | null {
//   if (!time) return null; // If time is null, return null
//   return selectedDate
//     .hour(time.hour())
//     .minute(time.minute())
//     .second(time.second());
// }

export const updateDatePreservingTime = (
  selectedDate: Dayjs,
  time: Dayjs | null,
  timezone: string = dayjs.tz.guess(),
): Dayjs | null => {
  if (!time) return null;

  const updatedTime = selectedDate
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second());

  return updatedTime.tz(timezone, true); // Retain local timezone
};

export function formatDateAndTimeFromString(dateString: string): string {
  if (dateString === undefined || dateString === "") {
    return "-";
  }
  const date = dayjs.utc(dateString).local(); // Parse the date as UTC and convert to local time

  // Format the date and time using the desired format string
  const formattedDateTime = date.format("D MMM YYYY, h:mm A");

  return formattedDateTime;
}
export function formatDateAndTimeFromString1hr(dateString: string): string {
  if (dateString === undefined || dateString === "") {
    return "-";
  }
  const date = dayjs.utc(dateString); // Parse the date as UTC and convert to local time

  // Format the date and time using the desired format string
  const formattedDateTime = date.format("D MMM YYYY, h:mm A");

  return formattedDateTime;
}
